/**
 * LiveCallModal — full-screen live audio/video call powered by LiveKit.
 *
 * Connects to a LiveKit room with the token minted server-side, renders the
 * remote participant (video) plus a local picture-in-picture preview, and
 * exposes mic / camera / hang-up controls. On hang-up it reports the call
 * duration so the parent can close the session in the database.
 */
import { useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import type { Room, RemoteTrack, LocalTrack } from "livekit-client";
import { Mic, MicOff, Video, VideoOff, PhoneOff, Loader2 } from "lucide-react";

const GRAD = "#5048E5";

interface Props {
  url: string;
  token: string;
  kind: "video" | "voice";
  otherName: string;
  onEnd: (durationSeconds: number, answered: boolean) => void;
}

function fmt(s: number) {
  const m = Math.floor(s / 60);
  const sec = (s % 60).toString().padStart(2, "0");
  return `${m}:${sec}`;
}

function removeMediaElements(container: HTMLDivElement | null) {
  container?.querySelectorAll("video,audio").forEach((el) => {
    if (el instanceof HTMLMediaElement) {
      el.pause();
      el.srcObject = null;
      el.removeAttribute("src");
      el.load();
    }
    el.remove();
  });
  if (container) container.innerHTML = "";
}

function detachTrack(track: RemoteTrack | LocalTrack | null | undefined, stopLocal = false) {
  if (!track) return;
  try {
    track.detach().forEach((el) => {
      if (el instanceof HTMLMediaElement) {
        el.pause();
        el.srcObject = null;
        el.removeAttribute("src");
        el.load();
      }
      el.remove();
    });
  } catch {
    /* best-effort media cleanup */
  }
  if (stopLocal) {
    try { (track as LocalTrack).stop(); } catch { /* already stopped */ }
  }
}

function cleanupRoom(room: Room | null, localNode: HTMLDivElement | null, remoteNode: HTMLDivElement | null) {
  if (!room) {
    removeMediaElements(localNode);
    removeMediaElements(remoteNode);
    return;
  }
  try {
    room.localParticipant.trackPublications.forEach((pub) => detachTrack(pub.track, true));
    room.remoteParticipants.forEach((participant) => {
      participant.trackPublications.forEach((pub) => detachTrack(pub.track, false));
    });
  } catch {
    /* best-effort media cleanup */
  }
  removeMediaElements(localNode);
  removeMediaElements(remoteNode);
  try { room.disconnect(); } catch { /* already disconnected */ }
}

export default function LiveCallModal({ url, token, kind, otherName, onEnd }: Props) {
  const roomRef = useRef<Room | null>(null);
  const localRef = useRef<HTMLDivElement>(null);
  const remoteRef = useRef<HTMLDivElement>(null);
  const startRef = useRef(Date.now());
  const endedRef = useRef(false);
  const remoteEverJoinedRef = useRef(false);
  const answeredRef = useRef(false);
  const hangupRef = useRef<() => void>(() => {});

  const [status, setStatus] = useState<"connecting" | "connected" | "error">("connecting");
  const [error, setError] = useState<string | null>(null);
  const [micOn, setMicOn] = useState(true);
  const [camOn, setCamOn] = useState(kind === "video");
  const [remoteJoined, setRemoteJoined] = useState(false);
  const [seconds, setSeconds] = useState(0);
  const [retryNonce, setRetryNonce] = useState(0);
  const portalNode = typeof document === "undefined" ? null : document.body;

  // The call clock only starts once the other person actually answers
  // (a remote participant joins). Until then the caller sees "Ringing…".
  const markAnswered = () => {
    if (answeredRef.current) return;
    answeredRef.current = true;
    startRef.current = Date.now();
    setSeconds(0);
  };

  useEffect(() => {
    const t = setInterval(() => {
      if (!answeredRef.current) return;
      setSeconds(Math.floor((Date.now() - startRef.current) / 1000));
    }, 1000);
    return () => clearInterval(t);
  }, []);


  useEffect(() => {
    let cancelled = false;
    let room: Room | null = null;

    setStatus("connecting");
    setError(null);

    (async () => {
      const { Room: RoomCtor, RoomEvent, Track } = await import("livekit-client");
      if (cancelled) return;
      room = new RoomCtor({ adaptiveStream: true, dynacast: true });
      roomRef.current = room;

      const attach = (track: RemoteTrack | LocalTrack, container: HTMLDivElement | null, isLocal: boolean) => {
        if (!container) return;
        if (track.kind === Track.Kind.Video) {
          const el = track.attach();
          el.style.width = "100%";
          el.style.height = "100%";
          el.style.objectFit = "cover";
          if (isLocal) (el as HTMLVideoElement).muted = true;
          container.innerHTML = "";
          container.appendChild(el);
        } else if (track.kind === Track.Kind.Audio && !isLocal) {
          const el = track.attach();
          el.style.display = "none";
          container.appendChild(el);
        }
      };

      room.on(RoomEvent.TrackSubscribed, (track) => {
        remoteEverJoinedRef.current = true;
        markAnswered();
        setRemoteJoined(true);
        attach(track, remoteRef.current, false);
      });
      room.on(RoomEvent.ParticipantConnected, () => {
        remoteEverJoinedRef.current = true;
        markAnswered();
        setRemoteJoined(true);
      });
      room.on(RoomEvent.ParticipantDisconnected, () => {
        if (room && room.numParticipants <= 1) {
          setRemoteJoined(false);
          // The other person hung up / left — end the call on this side too.
          if (remoteEverJoinedRef.current) hangupRef.current();
        }
      });
      // Our own connection was terminated (e.g. server closed the room).
      room.on(RoomEvent.Disconnected, () => {
        if (remoteEverJoinedRef.current) hangupRef.current();
      });
      room.on(RoomEvent.LocalTrackPublished, (pub) => {
        if (pub.track) attach(pub.track, localRef.current, true);
      });

      // The signalling WebSocket can fail transiently during establishment
      // (network blips, proxies). Retry a few times before surfacing an error.
      const MAX_ATTEMPTS = 4;
      let lastErr: unknown = null;
      for (let attempt = 1; attempt <= MAX_ATTEMPTS; attempt++) {
        if (cancelled) return;
        try {
          await room.connect(url, token);
          if (cancelled) return;
          await room.localParticipant.setMicrophoneEnabled(true);
          if (kind === "video") await room.localParticipant.setCameraEnabled(true);
          if (cancelled) return;
          setStatus("connected");
          setError(null);
          // If the other party is already in the room, they've answered.
          if (room.numParticipants > 0) {
            markAnswered();
            setRemoteJoined(true);
          }
          return;
        } catch (e) {
          lastErr = e;
          if (cancelled) return;
          // Surface progress while we keep trying.
          if (attempt < MAX_ATTEMPTS) {
            await new Promise((r) => setTimeout(r, 800 * attempt));
          }
        }
      }

      if (cancelled) return;
      const msg = lastErr instanceof Error ? lastErr.message : "";
      setError(
        /websocket|signal connection|fetch/i.test(msg)
          ? "Couldn't reach the call server. This is usually a network or firewall issue — check your connection and try again."
          : msg || "Could not connect to the call.",
      );
      setStatus("error");
    })();

    return () => {
      cancelled = true;
      cleanupRoom(room, localRef.current, remoteRef.current);
    };
  }, [url, token, kind, retryNonce]);


  function hangup() {
    if (endedRef.current) return;
    endedRef.current = true;
    // Only count time once the call was actually answered.
    const answered = answeredRef.current;
    const dur = answered ? Math.floor((Date.now() - startRef.current) / 1000) : 0;
    cleanupRoom(roomRef.current, localRef.current, remoteRef.current);
    roomRef.current = null;
    onEnd(dur, answered);
  }
  hangupRef.current = hangup;

  function retry() {
    cleanupRoom(roomRef.current, localRef.current, remoteRef.current);
    roomRef.current = null;
    answeredRef.current = false;
    remoteEverJoinedRef.current = false;
    setSeconds(0);
    setRemoteJoined(false);
    setRetryNonce((n) => n + 1);
  }


  async function toggleMic() {
    const lp = roomRef.current?.localParticipant;
    if (!lp) return;
    const next = !micOn;
    await lp.setMicrophoneEnabled(next);
    setMicOn(next);
  }

  async function toggleCam() {
    const lp = roomRef.current?.localParticipant;
    if (!lp) return;
    const next = !camOn;
    await lp.setCameraEnabled(next);
    setCamOn(next);
    if (!next && localRef.current) localRef.current.innerHTML = "";
  }

  const initials = otherName.split(" ").map((n) => n[0]).join("").toUpperCase().slice(0, 2);
  const showRemoteVideo = kind === "video" && remoteJoined;
  if (!portalNode) return null;

  return createPortal(
    <div className="fixed inset-0 z-[120] flex flex-col" style={{ background: "#0b0b14", opacity: 1 }}>
      {/* Remote / stage */}
      <div className="relative flex-1 min-h-0 overflow-hidden">
        <div ref={remoteRef} className="absolute inset-0" style={{ display: showRemoteVideo ? "block" : "none" }} />

        {!showRemoteVideo && (
          <div className="absolute inset-0 flex flex-col items-center justify-center gap-4">
            <span
              className="flex h-28 w-28 items-center justify-center rounded-full text-4xl font-bold"
              style={{ background: GRAD, color: "#fff", fontFamily: "Outfit, sans-serif" }}
            >
              {initials}
            </span>
            <p className="text-xl font-bold text-white" style={{ fontFamily: "Outfit, sans-serif" }}>{otherName}</p>
            <p className="text-sm text-white/60" style={{ fontFamily: "Plus Jakarta Sans, sans-serif" }}>
              {status === "connecting"
                ? "Connecting…"
                : status === "error"
                  ? "Connection problem"
                  : remoteJoined
                    ? kind === "voice" ? "On the call" : "Camera off"
                    : "Ringing…"}
            </p>
          </div>
        )}

        {/* Status pill */}
        <div className="absolute left-1/2 top-4 -translate-x-1/2 rounded-full px-3 py-1.5 text-xs font-semibold text-white" style={{ background: "rgba(0,0,0,0.45)", fontFamily: "Plus Jakarta Sans, sans-serif" }}>
          {status === "connecting" ? (
            <span className="flex items-center gap-1.5"><Loader2 size={12} className="animate-spin" /> Connecting…</span>
          ) : !remoteJoined ? (
            <span className="flex items-center gap-1.5"><Loader2 size={12} className="animate-spin" /> Ringing…</span>
          ) : (
            <>{kind === "video" ? "Video call" : "Voice call"} · {fmt(seconds)}</>
          )}
        </div>


        {/* Local PiP (video only) */}
        {kind === "video" && (
          <div
            ref={localRef}
            className="absolute bottom-4 right-4 h-40 w-28 overflow-hidden rounded-xl border-2 sm:h-48 sm:w-36"
            style={{ borderColor: "rgba(255,255,255,0.25)", background: "#15151f", display: camOn ? "block" : "none" }}
          />
        )}

        {error && (
          <div className="absolute bottom-24 left-1/2 w-[88%] max-w-md -translate-x-1/2 rounded-lg px-4 py-3 text-center text-sm text-white" style={{ background: "rgba(220,38,38,0.9)" }}>
            <p>{error}</p>
            {status === "error" && (
              <button
                onClick={retry}
                className="mt-2 inline-flex items-center rounded-full bg-white px-4 py-1.5 text-xs font-bold text-[#dc2626] transition-transform hover:scale-105"
                style={{ fontFamily: "Plus Jakarta Sans, sans-serif" }}
              >
                Try again
              </button>
            )}
          </div>
        )}
      </div>

      {/* Controls */}
      <div className="flex items-center justify-center gap-4 py-6" style={{ paddingBottom: "max(24px, env(safe-area-inset-bottom))" }}>
        <button
          onClick={toggleMic}
          aria-label={micOn ? "Mute microphone" : "Unmute microphone"}
          className="flex h-14 w-14 items-center justify-center rounded-full transition-colors"
          style={{ background: micOn ? "rgba(255,255,255,0.15)" : "#fff" }}
        >
          {micOn ? <Mic size={22} color="#fff" /> : <MicOff size={22} color="#0b0b14" />}
        </button>

        {kind === "video" && (
          <button
            onClick={toggleCam}
            aria-label={camOn ? "Turn camera off" : "Turn camera on"}
            className="flex h-14 w-14 items-center justify-center rounded-full transition-colors"
            style={{ background: camOn ? "rgba(255,255,255,0.15)" : "#fff" }}
          >
            {camOn ? <Video size={22} color="#fff" /> : <VideoOff size={22} color="#0b0b14" />}
          </button>
        )}

        <button
          onClick={hangup}
          aria-label="End call"
          className="flex h-14 w-14 items-center justify-center rounded-full transition-transform hover:scale-105"
          style={{ background: "#dc2626" }}
        >
          <PhoneOff size={24} color="#fff" />
        </button>
      </div>
    </div>,
    portalNode,
  );
}
